import { useCallback, useMemo, useState, useEffect } from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';
import Pagination from '@/components/pagination';
import { Meeting, SortField, SortState } from './past-meetings.types';
import '@/assets/styles/views/profile/past-meetings.scss';
import { useNavigate } from 'react-router';
import { NavigationPath } from '@/router/navigation-path.ts';
import { useApi } from '@/api/useApi.tsx';
import { useQueryProvider } from '@/hooks';
import { QUERY_KEYS } from '@/constants/QueryKeys.ts';
import { ViewType } from '@/screens/profile/profile-screen.types.ts';
import { getMonthDateRange, isUndefined } from '@/utility';
import { APP_TYPES, endDateTimezone, INTERACTION_STATUS_ICON, SERVICES_LIST, PAYMENT_TYPES, LIVE_SERVICE_TYPES, MEETING_TYPES } from '@/constants';
import { useSizes } from '@/utility/responsive.ts';
import { VIEWS_MAP } from '@/screens/profile/profile-screen.constants.ts';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import clsx from 'clsx';
import { useFeatureFlag } from 'configcat-react';

const ITEMS_PER_PAGE = 10;

type PastMeetingsType = {
	currentDate: any;
	activeView: ViewType;
	onOpenMonthlyEarningModal: () => void;
	isEarningsHidden: boolean;
};

const PastMeetings = ({ currentDate, activeView, onOpenMonthlyEarningModal, isEarningsHidden }: PastMeetingsType) => {
	const [currentPage, setCurrentPage] = useState(0);
	const [sortState, setSortState] = useState<SortState>({
		field: null,
		direction: 'asc'
	});

	const [isActiveQuestionInspect, setIsActiveQuestionInspect] = useState(false);
	const [selectedQuestionImage, setSelectedQuestionImage] = useState<string>('');
	const [hoveredEarningId, setHoveredEarningId] = useState<string | null>(null);

	const { InteractionControllerApi } = useApi();
	const { isDesktop } = useSizes();
	const { value: paymentTypeInfos } = useFeatureFlag('paymenttypeinfos', 'demo');

	const isSolver = useMemo(() => activeView === VIEWS_MAP.SOLVER, [activeView]);

	const isCreditRefund = useCallback(
		(meeting: Meeting) => meeting?.interactionStatus === 'CREDIT_REFUND' || meeting?.interactionStatusDescription === 'Kredi İade Edildi',
		[]
	);

	const getServiceType = useCallback((meeting: Meeting) => {
		if (meeting.liveServiceTypeId && SERVICES_LIST[meeting.liveServiceTypeId as keyof typeof SERVICES_LIST]) {
			return SERVICES_LIST[meeting.liveServiceTypeId as keyof typeof SERVICES_LIST];
		}

		if (meeting.liveServiceGroup === LIVE_SERVICE_TYPES.SOLVER) {
			return SERVICES_LIST[1];
		}
		if (meeting.liveServiceGroup === LIVE_SERVICE_TYPES.COACHING && meeting.meetingType === MEETING_TYPES.APPOINTMENT) {
			return SERVICES_LIST[4];
		}
		if (meeting.liveServiceGroup === LIVE_SERVICE_TYPES.COACHING && meeting.meetingType === MEETING_TYPES.INSTANT) {
			return SERVICES_LIST[3];
		}
		if (meeting.liveServiceGroup === LIVE_SERVICE_TYPES.GOT_IT && meeting.meetingType === MEETING_TYPES.APPOINTMENT) {
			return SERVICES_LIST[6];
		}
		if (meeting.liveServiceGroup === LIVE_SERVICE_TYPES.GOT_IT && meeting.meetingType === MEETING_TYPES.INSTANT) {
			return SERVICES_LIST[2];
		}

		return 'UNKNOWN';
	}, []);

	const getUnratedEarningTooltip = useCallback(
		(meeting: Meeting) => {
			const serviceType = getServiceType(meeting);

			if (!paymentTypeInfos) return '';

			let parsedInfos: Record<string, string> = {};

			if (typeof paymentTypeInfos === 'string') {
				try {
					parsedInfos = JSON.parse(paymentTypeInfos);
				} catch {
					return '';
				}
			} else if (typeof paymentTypeInfos === 'object') {
				parsedInfos = paymentTypeInfos as Record<string, string>;
			}

			return parsedInfos[serviceType] || '';
		},
		[getServiceType, paymentTypeInfos]
	);

	const sortParam = useMemo(() => {
		if (!sortState.field) return 'interactionStartDate,DESC';

		if (sortState.field === 'date') {
			return `interactionStartDate,${sortState.direction.toUpperCase()}`;
		}

		if (sortState.field === 'rating') {
			return `rating,${sortState.direction.toUpperCase()}`;
		}

		return 'interactionStartDate,DESC';
	}, [sortState]);

	const { data: pastMeetingData } = useQueryProvider(
		[QUERY_KEYS.findExpertInteractions, activeView, currentDate, currentPage, sortParam],
		() =>
			InteractionControllerApi.findExpertInteractions(
				getMonthDateRange(currentDate)?.startDate,
				getMonthDateRange(currentDate, undefined, endDateTimezone)?.endDate,
				undefined,
				activeView,
				currentPage,
				ITEMS_PER_PAGE,
				sortParam
			),
		{
			select: ({ data }) => data.data,
			refetchOnWindowFocus: true,
			keepPreviousData: true
		}
	);

	const navigate = useNavigate();

	const handleSort = (field: SortField) => {
		setSortState(prevState => ({
			field,
			direction: prevState.field === field && prevState.direction === 'asc' ? 'desc' : 'asc'
		}));
		setCurrentPage(0);
	};

	const handlePageChange = (page: number) => {
		setCurrentPage(page - 1);
	};

	useEffect(() => {
		setCurrentPage(0);
	}, [currentDate, activeView]);

	const totalPages = pastMeetingData?.totalPages || 0;
	const displayCurrentPage = currentPage + 1;

	const getRatingColor = useCallback((rating: number): string => {
		if (rating >= 4) return '#FFD012';
		if (rating === 3) return '#E67E22';
		return 'var(--danger-text)';
	}, []);

	const renderRatingStars = useCallback(
		(rating: number) => {
			const ratingColor = getRatingColor(rating);
			return [...Array(5)]?.map((_, index) => (
				<i
					key={index}
					className={`icons-star ${index < rating ? '' : 'inactive'}`}
					style={index < rating ? { color: ratingColor } : undefined}
				/>
			));
		},
		[getRatingColor]
	);

	const getSortIcon = (field: SortField) => {
		if (sortState.field === field) {
			return <i className={`icons-alt-arrow-${sortState.direction === 'asc' ? 'down' : 'up'}`} />;
		}
		return <i className="icons-alt-arrow-down" />;
	};

	const handleQuestionImageClick = (questionImage: string) => {
		setSelectedQuestionImage(questionImage);
		setIsActiveQuestionInspect(true);
	};

	const onClickMeeting = (meeting: Meeting) => () => {
		if (activeView !== VIEWS_MAP.SOLVER) {
			if (meeting?.liveServiceGroup === APP_TYPES.GOT_IT) {
				navigate(NavigationPath.gotItPastMeetingsDetail, {
					state: { meeting, interactionId: meeting?.id }
				});
			} else {
				navigate(NavigationPath.coachPastMeetingsDetail, {
					state: { meeting, interactionId: meeting?.id }
				});
			}
		}
	};

	const renderEarningColumn = useCallback(
		(meeting: Meeting, className: string = 'earning-card') => {
			const isHovered = hoveredEarningId === meeting.id;

			switch (meeting.paymentType) {
				case PAYMENT_TYPES.NOT_RATED: {
					const tooltipText = getUnratedEarningTooltip(meeting);
					return (
						<div
							className={`${className} special`}
							onMouseEnter={() => setHoveredEarningId(meeting.id || null)}
							onMouseLeave={() => setHoveredEarningId(null)}>
							<i className="icons-info-circle" />
							<span>Ort. puanınıza göre hesaplanacaktır.</span>
							{isHovered && <div className="custom-tooltip">{tooltipText}</div>}
						</div>
					);
				}

				case PAYMENT_TYPES.PARTIAL_PAYMENT: {
					if (!meeting.paymentAmount || meeting.paymentAmount === 0) {
						return (
							<div
								className={`${className} special`}
								onMouseEnter={() => setHoveredEarningId(meeting.id || null)}
								onMouseLeave={() => setHoveredEarningId(null)}>
								<i className="icons-info-circle" />
								<span>Ay sonunda hesaplanacaktır</span>
								{isHovered && <div className="custom-tooltip">Aylık gerçekleşen görüşme sayınızın %10'u kadar ödeme yapılır.</div>}
							</div>
						);
					}
					return <div className={`${className} ${meeting?.paymentAmount !== 0 && !isUndefined(meeting?.paymentAmount) ? 'hight' : ''}`}>₺{meeting?.paymentAmount || 0}</div>;
				}

				case PAYMENT_TYPES.NO_PAYMENT:
					return <div className={className}>₺0</div>;

				case PAYMENT_TYPES.FULL_PAYMENT:
					return <div className={`${className} ${meeting?.paymentAmount !== 0 && !isUndefined(meeting?.paymentAmount) ? 'hight' : ''}`}>₺{meeting?.paymentAmount || 0}</div>;

				default:
					if (!meeting.paymentType && (!meeting.paymentAmount || meeting.paymentAmount === 0)) {
						return <div className={className}></div>;
					}
					return <div className={`${className} ${meeting?.paymentAmount !== 0 && !isUndefined(meeting?.paymentAmount) ? 'hight' : ''}`}>₺{meeting?.paymentAmount || 0}</div>;
			}
		},
		[getUnratedEarningTooltip, hoveredEarningId]
	);

	const renderPastMeetingTable = () => {
		if (isDesktop) {
			return (
				<>
					{pastMeetingData?.content?.map((meeting, index) => (
						<div
							key={meeting.id}
							className="meeting-row"
							onClick={onClickMeeting(meeting)}>
							<div className="col-id">{currentPage * ITEMS_PER_PAGE + index + 1}</div>
							<div className="col-date">{dayjs(meeting.interactionStartDate).format('D MMMM, HH:mm')}</div>
							<div className="col-student">
								{isSolver ? (
									<img
										src={meeting?.questionImage}
										alt="Question"
										className="question-image"
										onClick={e => {
											e.stopPropagation();
											handleQuestionImageClick(meeting?.questionImage);
										}}
									/>
								) : (
									<div className="student-info">
										<div className="name">{`${meeting.studentName} ${meeting.studentSurname}`}</div>
									</div>
								)}
							</div>
							<div className="col-rating">
								{meeting.rating || meeting?.feedbackRating ? (
									<div className="stars">{renderRatingStars(meeting.feedbackRating || meeting.rating)}</div>
								) : (
									<span className="rating-badge">Henüz puanlanmadı</span>
								)}
							</div>
							<div className="col-status">
								{meeting?.isExternalMeetingReportCreated && (
									<span className="meeting-report-status">
										<i className="icons-videocamera-record" />
										Meet Görüşmesi
									</span>
								)}
								<span style={{ color: isCreditRefund(meeting) ? '#FF9800' : undefined }}>
									{meeting?.interactionStatusDescription}
								</span>
							</div>
							{!isEarningsHidden && <div className="col-earning">{renderEarningColumn(meeting)}</div>}
							<div className="student-wrapper">
								<div className="student-info">
									<div className="name">{`${meeting.studentName} ${meeting.studentSurname}`}</div>
								</div>
								{meeting.rating ? <div className="stars">{renderRatingStars(meeting.rating)}</div> : <span className="rating-badge">Henüz puanlanmadı</span>}
							</div>
							<div className="date-wrapper">{dayjs(meeting.interactionStartDate).format('D MMMM, HH:mm')}</div>
							<div className="meet-status-wrapper">
								<div className="status" style={{ color: isCreditRefund(meeting) ? '#FF9800' : undefined }}>{meeting?.interactionStatusDescription}</div>
								{!isEarningsHidden && renderEarningColumn(meeting)}
							</div>
						</div>
					))}
				</>
			);
		} else {
			return (
				<div>
					{pastMeetingData?.content?.map(meeting => (
						<div
							key={meeting.id}
							className={`mobile-meeting-row ${meeting?.meetingType}`}
							onClick={onClickMeeting(meeting)}>
							<div className={'col-header'}>
								<div className="col-header-student">
									<div className="student-info">
										{isSolver ? (
											<div className="col-question-mobile">
												<img
													src={meeting?.questionImage}
													alt="Question"
													className="question-image-mobile"
													onClick={e => {
														e.stopPropagation();
														handleQuestionImageClick(meeting?.questionImage);
													}}
												/>
											</div>
										) : (
											<div className="name">{`${meeting?.studentName} ${meeting?.studentSurname}`}</div>
										)}
									</div>
									<div className="col-header-information-date">
										<span>{dayjs(meeting.interactionStartDate).format('D MMMM, HH:mm')}</span>
									</div>
								</div>
								<div className={'col-header-information'}>
									<div className="col-header-information-rating">{meeting.rating ? <div className="stars">{renderRatingStars(meeting.rating)}</div> : null}</div>
								</div>
							</div>

							<div className={clsx('col-body', { 'solver-col-body': isSolver })}>
								<div className="col-status">
									{meeting?.isExternalMeetingReportCreated && (
										<span className="meeting-report-status">
											<i className="icons-videocamera-record" />
											Meet Görüşmesi
										</span>
									)}
									<span className="status-text" style={{ color: isCreditRefund(meeting) ? '#FF9800' : undefined }}>
										<i
											className={INTERACTION_STATUS_ICON[meeting?.interactionStatus]}
											style={{
												color: isCreditRefund(meeting) ? '#FF9800' : INTERACTION_STATUS_ICON[meeting?.interactionStatus] === 'icons-danger-circle' ? 'rgba(241, 82, 82, 1)' : 'rgba(26, 188, 156, 1)'
											}}
										/>
										{meeting?.interactionStatusDescription}
									</span>
								</div>
								{!isEarningsHidden && <div className="col-earning">{renderEarningColumn(meeting)}</div>}
							</div>

							{!!meeting?.meetingType && (
								<div className={'col-footer'}>
									<div className={'col-show-report'}></div>
								</div>
							)}
						</div>
					))}
				</div>
			);
		}
	};

	return (
		<div className="profile-meeting-history">
			<div className="page-header">
				<h1>{isSolver ? 'Gönderdiğim Sorular' : 'Görüşmelerim'}</h1>
				{!isEarningsHidden && (
					<button
						className="monthly-earning-button"
						onClick={onOpenMonthlyEarningModal}>
						<i className="icons-info-circle" />
						Aylık Kazanç Nasıl Hesaplanır?
					</button>
				)}
			</div>

			<div className={clsx('meetings-table', { 'no-earning': isEarningsHidden })}>
				<div className="table-header">
					<div className="col">No</div>
					<div
						className="col sortable"
						onClick={() => handleSort('date')}>
						<span>Tarihi</span>
						{getSortIcon('date')}
					</div>
					<div className="col">{isSolver ? 'Soru' : 'Öğrenci'}</div>

					<div
						className="col sortable"
						onClick={() => handleSort('rating')}>
						<span>Puan</span>
						{getSortIcon('rating')}
					</div>
					<div className="col">Durum</div>
					{!isEarningsHidden && <div className="col earning">Kazanç</div>}
				</div>

				<div className="table-body">{renderPastMeetingTable()}</div>
			</div>

			{totalPages > 1 && (
				<div className="pagination-container">
					<Pagination
						currentPage={displayCurrentPage}
						totalPages={totalPages}
						onPageChange={handlePageChange}
					/>
				</div>
			)}

			<Lightbox
				open={isActiveQuestionInspect}
				close={() => setIsActiveQuestionInspect(false)}
				carousel={{ finite: true }}
				controller={{ closeOnBackdropClick: true }}
				render={{
					buttonPrev: () => null,
					buttonNext: () => null
				}}
				slides={[{ src: selectedQuestionImage, width: 920, height: 520 }]}
			/>
		</div>
	);
};

export default PastMeetings;
